'use client'

import { useEffect, useRef, type RefObject } from 'react'
import styles from './AllergenTable.module.scss'
import useSyncedHorizontalScroll from '@/hooks/use-synced-horizontal-scroll'
import useWindowResize from '@/hooks/use-window-resize'
import useStore from '@/store'
import { measureStickyChromeHeight } from '@/components/ContentWithStickyNavigation/ContentWithStickyNavigation'
import type { AllergenTableColumn } from './AllergenTable'

type StickyHeaderBarProps = {
  columns: AllergenTableColumn[]
  // Element that receives `--allergen-sticky-top` (the table's root), shared with the desktop thead.
  rootRef: RefObject<HTMLElement | null>
  // The horizontal scroller whose offset the bar mirrors.
  scrollerRef: RefObject<HTMLElement | null>
}

// Visible header below `laptop`: the real <thead> is visually hidden there, and a sticky header
// inside an overflow-x scroller cannot stick to the page, so this bar follows the scroller instead.
const StickyHeaderBar = ({ columns, rootRef, scrollerRef }: StickyHeaderBarProps) => {
  const trackRef = useRef<HTMLDivElement | null>(null)
  const resizeKey = useWindowResize()
  const stickyBannerIsOpen = useStore(state => state.stickyBannerIsOpen)

  useEffect(() => {
    rootRef.current?.style.setProperty('--allergen-sticky-top', `${measureStickyChromeHeight()}px`)
  }, [rootRef, stickyBannerIsOpen, resizeKey])

  useSyncedHorizontalScroll(scrollerRef, trackRef)

  return (
    <div
      className={styles.stickyBar}
      aria-hidden="true"
    >
      <div className={styles.stickyBarCorner} />
      <div className={styles.stickyBarViewport}>
        <div
          ref={trackRef}
          className={styles.stickyBarTrack}
        >
          {columns.map(column => (
            <div
              key={column.key}
              className={styles.stickyBarCell}
            >
              {column.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

StickyHeaderBar.displayName = 'StickyHeaderBar'

export default StickyHeaderBar
